import {
  useEffect,
  ChangeEvent,
  useRef,
  useState,
  useMemo,
  useId,
} from 'react';

import classNames from 'classnames';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import { Box } from '../../Box';
import { Checkbox } from '../../Checkbox';
import { Badge, ClearButton, Label, Preloader } from '../common';
import { Typography } from '../../Typography';
import { Tooltip } from '../../Tooltip';

import useStyles from './SelectInput.styles';
import { MultipleAutocomplete, SingleAutocomplete } from './Autocompletes';
import { SelectInputSingleProps, SelectInputMultipleProps } from './types';
import { PopupIcon } from './PopupIcon';

type SelectInputProps<T> =
  | SelectInputSingleProps<T>
  | SelectInputMultipleProps<T>;

/**
 * SelectInput — это компонент, который предоставляет настраиваемый выбор ввода с вариантами для выбора одного или нескольких элементов.
 * Он поддерживает бесконечную прокрутку и может отображать значки (баджи) для вариантов. Также включает индикатор загрузки и кнопку очистки.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param items - Список элементов для выбора.
 * @param label - Текст метки для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param value - Текущее значение выбора ввода.
 * @param onChange - Функция, вызываемая при изменении значения.
 * @param onLogAction - Функция для логирования действий.
 * @param multiple - Флаг множественного выбора.
 * @param testId - Тестовый ID для выбора ввода.
 * @param disabled - Флаг, указывающий, отключен ли выбор ввода.
 * @param placeholder - Текст плейсхолдера для выбора ввода.
 * @param autocomplete - Флаг автодополнения.
 * @param required - Флаг обязательности поля.
 * @param clearable - Флаг возможности очистки.
 * @param loading - Флаг загрузки данных.
 * @param tooltip - Подсказка для метки.
 * @param inputTooltipText - Текст подсказки для поля ввода.
 * @param error - Флаг ошибки.
 * @param helperText - Вспомогательный текст.
 * @param renderOption - Пользовательская функция для рендера вариантов.
 * @param additionalOption - Дополнительный вариант, отображаемый, если есть элементы.
 * @param emptyOption - Пустой вариант, отображаемый, если нет элементов.
 * @param withoutMargins - Флаг для отключения отступов.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link SelectInputProps}
 */
export const SelectInput = <T extends string | number>({
  id,
  label,
  labelIcon,
  value,
  items,
  onChange,
  onLogAction,
  disabled = false,
  placeholder,
  testId,
  autocomplete = false,
  required = false,
  clearable = false,
  loading = false,
  tooltip,
  error = false,
  helperText,
  renderOption,
  inputTooltipText,
  additionalOption,
  emptyOption,
  withoutMargins = false,
  ...rest
  // eslint-disable-next-line complexity
}: SelectInputProps<T>) => {
  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const [inputValue, setInputValue] = useState('');
  const [width, setWidth] = useState('auto');

  const selectRef = useRef(undefined);
  const inputRef = useRef<HTMLDivElement | null>(null);

  const valueBadge = useMemo(
    () => items.find((item) => item.value === value)?.badge,
    [value, items],
  );

  const valueColorIndicator = useMemo(
    () => items.find((item) => item.value === value)?.color,
    [value, items],
  );

  const valueStartIcon = useMemo(
    () => items.find((item) => item.value === value)?.startIcon,
    [value, items],
  );

  const isThereSomeGroup = items.some((option) => option.group);

  const autocompleteFocusHandler = () => {
    setInputValue('');
  };

  useEffect(() => {
    if (!value) {
      setInputValue('');
    }
  }, [value]);

  useEffect(() => {
    setWidth(inputRef.current ? `${inputRef.current.clientWidth}px` : 'auto');
  }, []);

  const handlePopoverOpen = () => {
    onLogAction?.('Открыть выпадающий список');
  };

  const valueIfMultiple = rest.multiple && !value ? [] : value;

  return (
    <FormControl
      ref={inputRef}
      className={classNames(
        classes.selectInput,
        withoutMargins && classes.withoutMargins,
        'aqa_select_input',
      )}
    >
      {label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
          inputId={inputId}
        />
      )}

      <Tooltip
        placement="right"
        title={inputTooltipText || ''}
        disabled={!inputTooltipText}
      >
        <span>
          {autocomplete ? (
            <>
              {!rest.multiple && (
                <SingleAutocomplete
                  label={label}
                  value={value as T}
                  items={items}
                  onChange={onChange as (value: T | undefined) => void}
                  disabled={disabled}
                  placeholder={placeholder}
                  testId={testId}
                  clearable={clearable}
                  loading={loading}
                  error={error}
                  helperText={helperText}
                  renderOption={renderOption}
                  emptyOption={emptyOption}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  isThereSomeGroup={isThereSomeGroup}
                  inputId={inputId}
                  handlePopoverOpen={handlePopoverOpen}
                  valueBadge={valueBadge}
                  valueColorIndicator={valueColorIndicator}
                  onFocus={autocompleteFocusHandler}
                  {...rest}
                />
              )}

              {rest.multiple && (
                <MultipleAutocomplete
                  label={label}
                  value={value as T[]}
                  items={items}
                  onChange={onChange as (value: T[] | undefined) => void}
                  disabled={disabled}
                  placeholder={placeholder}
                  testId={testId}
                  clearable={clearable}
                  loading={loading}
                  error={error}
                  helperText={helperText}
                  renderOption={renderOption}
                  emptyOption={emptyOption}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  isThereSomeGroup={isThereSomeGroup}
                  inputId={inputId}
                  handlePopoverOpen={handlePopoverOpen}
                  valueBadge={valueBadge}
                  valueColorIndicator={valueColorIndicator}
                  onFocus={autocompleteFocusHandler}
                  {...rest}
                />
              )}
            </>
          ) : (
            <TextField
              {...rest}
              classes={{
                root: `${classes.inputRoot} ${
                  rest.multiple ? 'aqa_multiselect' : 'aqa_select'
                }`,
              }}
              data-testid={testId}
              select={!loading}
              placeholder={placeholder || label}
              value={loading ? 'Загрузка...' : valueIfMultiple}
              onChange={(event: ChangeEvent<{ value: unknown }>) => {
                if (rest.multiple) {
                  (onChange as (value: T[] | undefined) => void)(
                    event.target.value as T[],
                  );
                } else {
                  (onChange as (value: T | undefined) => void)(
                    event.target.value as T,
                  );
                }
              }}
              disabled={disabled || loading}
              variant="outlined"
              fullWidth
              error={error}
              helperText={helperText}
              slotProps={{
                input: {
                  inputRef: selectRef,
                  id: inputId,
                  endAdornment: loading ? (
                    <Preloader />
                  ) : (
                    !disabled &&
                    clearable &&
                    !!(rest.multiple ? (value as T[])?.length : value) && (
                      <ClearButton
                        className={classes.clearButton}
                        onClick={() => onChange(undefined)}
                        testId="select-input-clear"
                      />
                    )
                  ),
                  startAdornment: (
                    <>
                      {valueBadge && !loading && (
                        <Badge
                          title={valueBadge.title}
                          color={valueBadge.color}
                          description={valueBadge.description}
                        />
                      )}

                      {!valueBadge && !loading && valueStartIcon && (
                        <Box
                          mr="8"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {valueStartIcon}
                        </Box>
                      )}
                    </>
                  ),
                },
                select: {
                  IconComponent: PopupIcon,
                  onClose: () => {
                    setTimeout(() => {
                      (
                        document?.activeElement as Element & {
                          blur: () => void;
                        }
                      )?.blur();
                    }, 0);
                  },
                  onOpen: handlePopoverOpen,
                  multiple: rest.multiple,
                  displayEmpty: true,
                  MenuProps: {
                    classes: {
                      paper: classNames(classes.list, {
                        [classes.longList]: !!renderOption,
                      }),
                    },
                    anchorOrigin: {
                      vertical: 61,
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    slotProps: {
                      paper: {
                        style: {
                          width,
                        },
                      },
                    },
                  },
                  renderValue: (selected) => {
                    if (
                      (Array.isArray(selected) && !selected.length) ||
                      !(selected || typeof selected === 'number')
                    ) {
                      return (
                        <span className={classes.placeholder}>
                          {placeholder || label}
                        </span>
                      );
                    }

                    if (rest.multiple) {
                      if (Array.isArray(selected)) {
                        return selected
                          .map((selectedValue) => {
                            const selectedOption = items.find(
                              (option) => option.value === (selectedValue as T),
                            );
                            return selectedOption ? selectedOption.label : '-';
                          })
                          .join(', ');
                      }

                      return '';
                    } else {
                      const selectedOption = items.find(
                        (option) => option.value === (selected as T),
                      );

                      return selectedOption?.label || '';
                    }
                  },
                },
              }}
            >
              {items.map((item) => {
                if (item.disabled) {
                  return (
                    <MenuItem
                      key={String(item.value)}
                      disabled
                      className={classNames(classes.menuItem, {
                        [classes.multiple]: rest.multiple,
                        [classes.defaultOption]: !renderOption && !item.badge,
                      })}
                      onClick={(event) => {
                        if (item.disabled) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                      }}
                    >
                      {renderOption ? (
                        renderOption(item)
                      ) : (
                        <>
                          {item.badge && (
                            <Box mr="8">
                              <Badge
                                title={item.badge.title}
                                description={item.badge.description}
                                color={item.badge.color}
                              />
                            </Box>
                          )}
                          <Typography variant="body3">{item.label}</Typography>
                        </>
                      )}
                    </MenuItem>
                  );
                } else {
                  return (
                    <MenuItem
                      className={classNames(classes.menuItem, {
                        [classes.multiple]: rest.multiple,
                        [classes.defaultOption]: !renderOption && !item.badge,
                      })}
                      value={item.value}
                      key={String(item.value)}
                      // disabled={item.disabled}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      {rest.multiple && (
                        <Checkbox
                          checked={
                            Array.isArray(value) && value.includes(item.value)
                          }
                        />
                      )}
                      {renderOption ? (
                        renderOption(item)
                      ) : (
                        <>
                          {item.badge && (
                            <Box mr="8">
                              <Badge
                                title={item.badge.title}
                                description={item.badge.description}
                                color={item.badge.color}
                              />
                            </Box>
                          )}
                          <Typography variant="body3">{item.label}</Typography>
                        </>
                      )}
                    </MenuItem>
                  );
                }
              })}
              {additionalOption && !!items.length && (
                <MenuItem
                  disabled
                  className={classNames(
                    classes.menuItem,
                    classes.additionalOption,
                  )}
                  key="emptyOption"
                >
                  {additionalOption}
                </MenuItem>
              )}
              {emptyOption && !items.length && (
                <MenuItem
                  disabled
                  className={classNames(
                    classes.menuItem,
                    classes.additionalOption,
                  )}
                  key="emptyOption"
                >
                  {emptyOption}
                </MenuItem>
              )}
            </TextField>
          )}
        </span>
      </Tooltip>
    </FormControl>
  );
};
